import React from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import useOnlineStatus from '@/hooks/useOnlineStatus';

/**
 * Sticky amber strip that appears at the top of any dashboard page when the
 * browser is offline. Tells the doctor / patient that what they're seeing is
 * a cached snapshot — and that any actions they take (booking changes, slot
 * toggles, profile edits) will fail until they reconnect.
 *
 * Usage: drop <OfflineBanner /> at the top of a dashboard layout.
 */
export default function OfflineBanner() {
  const online = useOnlineStatus();
  if (online) return null;
  return (
    <div className="bg-amber-100 border-b border-amber-300 text-amber-900" data-testid="offline-banner" role="status" aria-live="polite">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <WifiOff size={14} className="flex-shrink-0" />
          <p className="text-xs font-semibold leading-tight">
            You're offline — showing cached data.
            <span className="hidden sm:inline font-normal text-amber-800/80"> &nbsp;Edits won't save until you reconnect.</span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-amber-300 text-amber-900 text-[11px] font-bold hover:bg-amber-50 transition-colors"
          data-testid="offline-banner-retry"
        >
          <RefreshCw size={11} /> Retry
        </button>
      </div>
    </div>
  );
}
